import { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { Button, TextInput } from "../ui";
import { createTowers } from "../../services/societyService";
import { towerListSchema, zodErrorToFirstMessage } from "../../schemas/formSchemas";
import { getStructureConfigByPropertyType } from "../../utils/propertyStructure";
import { Plus, Trash2 } from "lucide-react";
import styles from "./TowerForm.module.css";

const TowerForm = ({ societyId, towers = [], propertyType, readOnly = false, onSuccess, onBack }) => {
  const [towerList, setTowerList] = useState([]);
  const [loading, setLoading] = useState(false);

  const structureConfig = useMemo(
    () => getStructureConfigByPropertyType(propertyType),
    [propertyType]
  );

  const subdivisionLabel = structureConfig.subdivisionLabel || "Tower";
  const subdivisionPlural = structureConfig.subdivisionPlural || "Towers";

  useEffect(() => {
    if (towers?.length) {
      setTowerList(
        towers.map((t) => {
          if (typeof t === "object" && t !== null) {
            const rawName = String(t.tower_name || "");
            return {
              tower_id: t.tower_id ? Number(t.tower_id) : undefined,
              tower_name: rawName === "[object Object]" ? "" : rawName,
            };
          }
          const str = String(t || "");
          return { tower_name: str === "[object Object]" ? "" : str };
        })
      );
    } else {
      setTowerList([{ tower_name: "" }]);
    }
  }, [towers]);

  const handleTowerChange = (index, value) => {
    const updated = [...towerList];
    updated[index] = { ...updated[index], tower_name: value };
    setTowerList(updated);
  };

  const addTower = () => {
    setTowerList([...towerList, { tower_name: "" }]);
  };

  const removeTower = (index) => {
    if (towerList.length === 1) {
      toast.error(`At least one ${subdivisionLabel.toLowerCase()} required`);
      return;
    }
    setTowerList(towerList.filter((_, idx) => idx !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const filtered = towerList
      .filter((t) => String(t.tower_name || "").trim())
      .map((t) => ({
        tower_id: t.tower_id ? Number(t.tower_id) : undefined,
        tower_name: String(t.tower_name || "").trim(),
      }));

    const parsed = towerListSchema.safeParse({
      society_id: societyId,
      towers: filtered,
    });
    if (!parsed.success) {
      toast.error(zodErrorToFirstMessage(parsed.error));
      return;
    }

    try {
      setLoading(true);
      const res = await createTowers(parsed.data);
      onSuccess?.(res);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <h3 className={styles.heading}>
        {readOnly ? `View ${subdivisionPlural}` : `Edit ${subdivisionPlural}`}
      </h3>

      {towerList.map((tower, index) => (
        <div key={tower.tower_id ? `tower-${tower.tower_id}` : `idx-${index}`} className={styles.row}>
          <span className={styles.index}>{index + 1}</span>

          <div style={{ flex: 1 }}>
            <TextInput
              value={tower.tower_name || ""}
              disabled={readOnly}
              placeholder={`Enter ${subdivisionLabel.toLowerCase()} name...`}
              onChange={(e) => handleTowerChange(index, e.target.value)}
            />
          </div>

          {!readOnly && towerList.length > 1 && (
            <Button
              type="button"
              variant="danger"
              size="sm"
              onClick={() => removeTower(index)}
              title={`Remove ${subdivisionLabel}`}
              icon={<Trash2 size={14} />}
            />
          )}
        </div>
      ))}

      {!readOnly && (
        <Button
          type="button"
          variant="outline"
          onClick={addTower}
          icon={<Plus size={15} />}
        >
          Add {subdivisionLabel}
        </Button>
      )}

      <div className={styles.actions}>
        {!!onBack && (
          <Button type="button" onClick={onBack} variant="outline">
            Back
          </Button>
        )}
        {!readOnly && (
          <Button type="submit" loading={loading} variant="primary">
            Save {subdivisionPlural}
          </Button>
        )}
      </div>
    </form>
  );
};

export default TowerForm;
